<template>
  <div
    id="vacancies"
    :class="$style.component"
    :style="componentStyle"
    data-job-list
  >
    <div :class="$style.mask" :style="maskStyle"></div>

    <div :class="$style.wrapper">
      <header :class="$style.header">
        <a href="#vacancies">
          <div>
            <h2
              :class="$style.title"
              :style="componentTitleStyle"
              v-html="i18n.title"
            ></h2>
          </div>
        </a>

        <div>
          <div v-if="shouldRenderAdvancedFilters">
            <advanced-filters
              :options="advancedFilterOptions"
              :dropdowns="dropdowns"
              :query="filterQuery"
              @search="onQueryChange"
              @select="onFilterSelect"
            />
          </div>

          <div v-if="shouldRenderTypeSelector">
            <base-button
              :theme="view.theme"
              :selected="selectedType === departmentKey"
              @click="selectType(departmentKey)"
            >
              {{ i18n.departments }}
            </base-button>

            <base-button
              :theme="view.theme"
              :selected="selectedType === locationKey"
              @click="selectType(locationKey)"
            >
              {{ i18n.locations }}
            </base-button>

            <base-button
              :theme="view.theme"
              :selected="selectedType === jobTypeKey"
              @click="selectType(jobTypeKey)"
            >
              {{ i18n.jobTypes }}
            </base-button>
          </div>
        </div>
      </header>

      <div :class="separatorClassname"></div>

      <tags-filter
        v-show="tags.length"
        :tags="tags"
        :options="{ i18n, view, css }"
        @remove="restoreTag"
        @reset="resetAllTags"
      />

      <div v-if="hasJobs && hasVisibleJobs" :class="$style.listWrapper">
        <div v-if="view.groupBy === 'none'">
          <job-list-items
            :items="listMap.others.vacancies"
            :options="listsOptions"
          />
        </div>
        <div v-else>
          <job-list-group
            v-for="group in listMap.groups"
            :key="group.id"
            :group="group"
            :options="listsOptions"
          />
          <job-list-group :group="listMap.others" :options="listsOptions" />
        </div>
      </div>
      <div
        v-else-if="hasJobs && !hasVisibleJobs"
        :class="
          view.theme === 'light' ? $style.lightEmptyState : $style.emptyState
        "
      >
        <h2 :class="$style.emptyStateTitle">
          {{
            filterQuery
              ? `${i18n.emptyStateTitleSearch} "${filterQuery}"`
              : i18n.emptyStateTitleFilters
          }}
        </h2>
        <p :class="$style.emptyStateText">
          {{
            filterQuery
              ? i18n.emptyStateMessageSearch
              : i18n.emptyStateMessageFilters
          }}
        </p>
        <base-button :theme="view.theme" @click="resetAdvancedFilters">
          {{ i18n.clearAll }}
        </base-button>
      </div>

      <div
        v-else
        :class="
          view.theme === 'light' ? $style.lightEmptyState : $style.emptyState
        "
      >
        <p :class="$style.emptyStateTitle">{{ i18n.noVacancies }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import cx from 'classnames'
import {content, css, i18n, view} from './types/job-list'
import JobListGroup from './JobListGroup'
import JobListItems from './JobListItems'
import AdvancedFilters from './AdvancedFilters'
import TagsFilter from './TagsFilter'
import BaseButton from './BaseButton'

import {
  parseQueryParams,
  constructUrl,
  scrollToAnchor
} from '../common/helpers'

export default {
  name: 'JobList',

  components: {
    BaseButton,
    AdvancedFilters,
    TagsFilter,
    JobListGroup,
    JobListItems
  },

  props: {
    content,
    css,
    view,
    i18n
  },

  data() {
    return {
      departmentKey: 'department',
      locationKey: 'location',
      jobTypeKey: 'jobType',
      advancedFilterOptions: {
        searchPlaceholder: this.i18n.findJobs
      },
      filterQuery: '',
      tags: [],
      vacancies: [],
      listView: this.view.listView,
      selectedType: this.view.groupBy,
      dropdowns: [],
      queryParams: {},
      allowedQueryParams: {
        tags: true,
        filterQuery: true,
        selectedType: true
      },
      saveFilterQueryParamDebounce: null
    }
  },

  computed: {
    transformedVacancies() {
      return this.content.vacancies.map((v) => {
        return {
          ...v,
          [this.departmentKey]: {
            id: v.department_id,
            position: v.position_departments
          },
          [this.jobTypeKey]: {
            id: v.job_type_id,
            position: v.position_job_types
          },
          [this.locationKey]: {
            id: v.location_id,
            position: v.position_locations
          }
        }
      })
    },

    departments() {
      return this.filterItemsOn(this.content.departments, 'department')
    },
    locations() {
      return this.filterItemsOn(this.content.locations, 'location')
    },
    jobTypes() {
      return this.filterItemsOn(this.content.job_types, 'jobType')
    },

    dropdownsModel() {
      const dropdowns = []

      if (this.departments.length) {
        dropdowns.push({
          id: this.departmentKey,
          label: this.i18n.departments,
          options: this.departments.map((d) => d.name)
        })
      }

      if (this.locations.length) {
        dropdowns.push({
          id: this.locationKey,
          label: this.i18n.locations,
          options: this.locations.map((d) => d.name)
        })
      }

      return dropdowns.concat({
        id: this.jobTypeKey,
        label: this.i18n.jobTypes,
        options: this.jobTypes.map((d) => d.name)
      })
    },

    separatorClassname() {
      return this.view.theme === 'dark'
        ? this.$style.separator
        : this.$style.lightSeparator
    },

    shouldRenderTypeSelector() {
      const { groupBy, advancedFilters } = this.view
      return groupBy !== 'none' && !advancedFilters
    },

    shouldRenderAdvancedFilters() {
      return this.view.advancedFilters
    },

    types() {
      switch (this.selectedType) {
        case this.departmentKey:
          return this.departments
        case this.locationKey:
          return this.locations
        case this.jobTypeKey:
          return this.jobTypes
      }
    },

    typesMap() {
      return {
        departments: this.mapItemsBy(this.departments, 'id'),
        locations: this.mapItemsBy(this.locations, 'id'),
        jobTypes: this.mapItemsBy(this.jobTypes, 'id')
      }
    },

    listMap() {
      const map = this.vacancies.reduce(
        (coll, vacancy) => {
          if (!vacancy.type) {
            coll.others.vacancies.push(vacancy)
            return coll
          }

          if (!coll.groups[vacancy.type.id]) {
            coll.groups[vacancy.type.id] = { ...vacancy.type, vacancies: [] }
          }

          coll.groups[vacancy.type.id].vacancies.push(vacancy)
          return coll
        },
        {
          others: { id: 'others', name: 'Others', vacancies: [] },
          groups: {}
        }
      )

      // Order vacancies per position
      map.others.vacancies.sort((a, b) => a.position - b.position)

      // Create an array from the groups map and order groups and vacancies based on position
      map.groups = Object.keys(map.groups)
        .reduce((coll, key) => {
          const group = map.groups[key]

          group.vacancies.sort((a, b) => {
            return a.position - b.position
          })

          coll.push(group)
          return coll
        }, [])
        .sort((a, b) => a.position - b.position)

      return map
    },

    moduleStyle() {
      return this.css.modules.vacancies
    },

    headingsStyle() {
      return this.css.headings
    },

    borderRadiusCareerFilter() {
      return this.css.borderRadius.career_filter
    },

    borderRadiusCareerGrid() {
      return this.css.borderRadius.career_grid
    },

    borderRadiusCareerList() {
      return this.css.borderRadius.career_list
    },

    componentStyle() {
      const style = {
        backgroundColor: this.moduleStyle.backgroundColor,
        paddingTop: this.moduleStyle.paddingTop,
        paddingBottom: this.moduleStyle.paddingBottom
      }

      if (this.moduleStyle.backgroundImage) {
        style.backgroundImage = `url(${this.moduleStyle.backgroundImage})`
      }

      return style
    },

    componentTitleStyle() {
      return {
        fontFamily: this.headingsStyle.fontFamily,
        fontWeight: this.headingsStyle.fontWeight,
        fontSize: this.headingsStyle.fontSize,
        lineHeight: this.headingsStyle.lineHeight,
        letterSpacing: this.headingsStyle.letterSpacing,
        color: this.moduleStyle.headingColor
      }
    },

    maskStyle() {
      return {
        backgroundColor: this.moduleStyle.backgroundColor,
        opacity: this.moduleStyle.backgroundImage
          ? this.moduleStyle.backgroundOpacity
          : 1
      }
    },

    listsOptions() {
      return {
        css: this.css,
        view: this.view
      }
    },

    visibleJobsCounter() {
      const activeJobs = this.vacancies.filter(
        ({ visibility: v }) => v.query && v.tags
      )
      return activeJobs.length
    },

    hasVisibleJobs() {
      return Boolean(this.visibleJobsCounter)
    },

    hasJobs() {
      return Boolean(this.vacancies.length)
    }
  },

  watch: {
    selectedType: function(type) {
      this.saveQueryParam('selectedType', type)
      this.vacancies = this.getVacancies()
    },
    tags: {
      deep: true,
      handler(tags) {
        this.applyTagsFilter(tags)

        this.saveQueryParam('tags', tags)
      }
    },
    filterQuery: function(query) {
      this.applyQueryFilter(query)

      if (this.saveFilterQueryParamDebounce) {
        clearTimeout(this.saveFilterQueryParamDebounce)
      }
      this.saveFilterQueryParamDebounce = setTimeout(() => {
        this.saveFilterQueryParamDebounce = null
        if (!query) {
          this.removeQueryParam('filterQuery')
          return
        }
        this.saveQueryParam('filterQuery', query)
      }, 500)
    }
  },

  created() {
    this.vacancies = this.getVacancies()
    this.dropdowns = cloneDeep(this.dropdownsModel)
    this.applyInitialQueryParams(window.location.search)
  },

  mounted() {
    scrollToAnchor()
  },

  methods: {
    pushQueryParamsToUrl(queryParams) {
      const queryString = Object.keys(queryParams).reduce((acc, key) => {
        let value = queryParams[key]
        if (key === 'tags') {
          acc += this.tagsToQueryParamString(value)
          return acc
        }

        acc += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`
        return acc
      }, '?')

      const newUrl = constructUrl(queryString)
      window.history.replaceState({ path: newUrl }, '', newUrl)
    },

    tagsToQueryParamString(tags) {
      return tags.reduce((acc, tag) => {
        const value = encodeURIComponent(`${tag.id},${tag.value}`)
        const key = encodeURIComponent('tags[]')
        acc += `${key}=${value}&`
        return acc
      }, '')
    },

    removeQueryParam(key) {
      delete this.queryParams[key]
      this.pushQueryParamsToUrl(this.queryParams)
    },

    saveQueryParam(key, value) {
      this.queryParams[key] = value
      this.pushQueryParamsToUrl(this.queryParams)
    },

    applyInitialQueryParams(queryParamsString) {
      const params = parseQueryParams(queryParamsString)

      this.queryParams = {}

      Object.keys(params).forEach((key) => {
        const value =
          key === 'tags'
              ? this.parseTagQueryParameter(params[key])
              : params[key]

        // Save in object so we can modify the URL
        this.queryParams[key] = value

        // And set actual values if allowed for component
        if (!this.allowedQueryParams[key]) {
          return
        }

        if (key === 'tags') {
          value.forEach(this.onFilterSelect)
          return
        }

        this[key] = value
      })
    },

    parseTagQueryParameter(tagStrings) {
      return tagStrings.reduce((acc, tagString) => {
        if (tagString.indexOf(',') < 0) {
          return acc
        }

        const [id, value] = tagString.split(',')
        const dropdown = this.dropdowns.find((d) => d.id === id)
        if (!dropdown || dropdown.options.findIndex((o) => o === value) < 0) {
          return acc
        }

        acc.push({ id, value })
        return acc
      }, [])
    },

    filterItemsOn(items, key) {
      const map = this.transformedVacancies.reduce((acc, v) => {
        // Take id of the field (department/location/jobType)
        acc[v[key].id] = true
        return acc
      }, {})
      return items.filter((i) => map[i.id])
    },

    mapItemsBy(items, key) {
      return items.reduce((acc, item) => {
        acc[item[key]] = item
        return acc
      }, {})
    },

    getVacancies() {
      return this.transformedVacancies.map((vacancy) => {
        const viewPropMatch = vacancy[this.selectedType]

        const position =
          this.selectedType === 'none'
            ? vacancy.position
            : viewPropMatch.position

        const type =
          viewPropMatch && viewPropMatch.id !== null
            ? this.types.find((t) => t.id === viewPropMatch.id)
            : null

        return {
          id: vacancy.id,
          title: vacancy.title,
          url: vacancy.url,
          visibility: { query: true, tags: true },
          displayTypes: this.getTypes(vacancy),
          queryTypes: this.getTypes(vacancy, { allTypes: true }).join(' '),
          tags: this.getTags(vacancy),
          position,
          type
        }
      })
    },

    selectType(type) {
      this.selectedType = type
    },

    getButtonState(type) {
      const dark = {
        button: this.$style.button,
        buttonSelected: this.$style.buttonSelected
      }

      const light = {
        button: this.$style.lightButton,
        buttonSelected: this.$style.lightButtonSelected
      }

      const theme = this.view.theme === 'dark' ? dark : light
      return cx({
        [theme.buttonSelected]: this.selectedType === type,
        [theme.button]: this.selectedType !== type
      })
    },

    getTypes(vacancy, { allTypes = false } = {}) {
      const types = []
      const location = this.typesMap.locations[vacancy.location.id]
      const department = this.typesMap.departments[vacancy.department.id]
      const jobType = this.typesMap.jobTypes[vacancy.jobType.id]

      if (allTypes) {
        return [location, department, jobType]
          .filter(Boolean)
          .map((t) => t.name)
      }

      if (this.selectedType === this.jobTypeKey) {
        types.push(location, department)
      } else if (this.selectedType === this.locationKey) {
        types.push(jobType, department)
      } else {
        types.push(jobType, location)
      }

      return types.map((t) => (t ? t.name : '')).filter(Boolean)
    },

    getTags(vacancy) {
      const department = this.typesMap.departments[vacancy.department.id]
      const location = this.typesMap.locations[vacancy.location.id]
      const jobType = this.typesMap.jobTypes[vacancy.jobType.id]

      return {
        department: department && department.name,
        location: location && location.name,
        jobType: jobType && jobType.name
      }
    },

    applyQueryFilter(query) {
      // If for some reason people are able to misuse the query parameter to fill this
      // default back to an empty string and run it again.
      if (typeof query !== 'string') {
        this.filterQuery = ''
        return
      }

      const matchValue = (value) => new RegExp(query.trim(), 'i').test(value)

      this.vacancies = this.vacancies.map((vacancy) => {
        const match =
          matchValue(vacancy.title) || matchValue(vacancy.queryTypes)

        vacancy.visibility.query = match
        return vacancy
      })
    },

    getTagsMatch(filterTags = [], tag = '') {
      const filterTypeTags = filterTags.map((t) => t.value)

      return filterTags.length ? filterTypeTags.some((t) => t === tag) : true
    },

    applyTagsFilter(tags) {
      // All possible tag types
      const types = [this.departmentKey, this.locationKey, this.jobTypeKey]
      // A map of all tags based on type
      const typesMap = types.reduce((coll, type) => {
        coll[type] = tags.filter((t) => t.id === type).map((t) => t.value)
        return coll
      }, {})

      this.vacancies = this.vacancies.map((vacancy) => {
        const isVisible = types.every((type) => {
          const typeTags = typesMap[type]

          return typeTags.length
            ? typeTags.some((tag) => tag === vacancy.tags[type])
            : true
        })

        vacancy.visibility.tags = isVisible

        return vacancy
      })
    },

    onQueryChange(value) {
      this.filterQuery = value
    },

    onFilterSelect(item) {
      const dropdown = this.dropdowns.find((d) => d.id === item.id)
      const optionIndex = dropdown.options.findIndex((v) => v === item.value)

      dropdown.options.splice(optionIndex, 1)

      this.tags.push(item)
    },

    restoreTag(tag) {
      const dropdown = this.dropdowns.find((d) => d.id === tag.id)
      const tagIndex = this.tags.findIndex((t) => t.value === tag.value)

      if (dropdown) {
        dropdown.options.push(tag.value)
      }

      if (tagIndex >= 0) {
        this.tags.splice(tagIndex, 1)
      }
    },

    resetAllTags() {
      this.tags = []
      this.dropdowns = cloneDeep(this.dropdownsModel)
    },

    resetAdvancedFilters() {
      this.filterQuery = ''
      this.resetAllTags()
    }
  }
}
</script>

<style module>
.component {
  position: relative;
  background-position: 50% 50%;
  background-size: cover;
  font-family: 'Inter', sans-serif;
  --border-radius-career-filter: v-bind('borderRadiusCareerFilter');
  --border-radius-career-grid: v-bind('borderRadiusCareerGrid');
  --border-radius-career-list: v-bind('borderRadiusCareerList');
}

.wrapper {
  position: relative;
  max-width: 1080px;
  margin: 0 auto;
  padding: 80px 30px 60px 30px;
  z-index: 1;
}

.title {
  margin: 0 0 23px 0;
  font-size: 32px;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.separator {
  display: none;
  height: 1px;
  margin: 20px 0 0 0;
  background: rgba(0, 0, 0, 0.1);
}

.lightSeparator {
  composes: separator;
  background: rgba(255, 255, 255, 0.2);
}

.listWrapper {
  margin-top: 20px;
}

.emptyState {
  text-align: center;
  padding: 60px 20px 40px 20px;
  color: rgba(0, 0, 0, 0.7);
}

.lightEmptyState {
  composes: emptyState;
  color: rgba(255, 255, 255, 0.7);
}

.emptyStateTitle {
  font-size: 24px;
  font-weight: 400;
  margin: 0;
}

.emptyStateText {
  font-size: 17px;
  margin: 10px 0 20px 0;
}

@media screen and (min-width: 960px) {
  .sticky {
    position: sticky;
    top: 0;
    display: block;
    background: $yellow;
    padding: 20px 0;
    z-index: 9;
  }

  .stickyNavigation {
    composes: sticky;
    top: 60px;
  }

  .header {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-gap: 20px;
    position: relative;
  }

  .title {
    margin: 0;
  }

  .separator {
    display: block;
  }
}
</style>
